import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { DashboardShell } from '../../components/DashboardShell.js';
import { useAuthStore } from '../../lib/auth.js';
import { useCurrentOrg } from '../../lib/orgs.js';
import { api, ApiClientError } from '../../lib/api.js';
import { OnboardingPage } from './OnboardingPage.js';

export function SettingsPage() {
  const { t } = useTranslation('dashboard');
  const { lang } = useParams();
  const navigate = useNavigate();
  const org = useCurrentOrg();

  const stripe = useMutation({
    mutationFn: () =>
      api<{ url: string }>(`/orgs/${org?.id}/stripe/onboarding`, {
        method: 'POST',
      }),
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
  });

  if (!org) return <OnboardingPage />;

  return (
    <DashboardShell>
      <h1 className="text-3xl font-bold mb-8">{t('settings_page.title')}</h1>

      <AccountSection orgId={org.id} role={org.role} />

      <section className="card mb-6">
        <h2 className="font-semibold mb-2">{t('settings_page.language_title')}</h2>
        <p className="text-sm text-neutral-400 mb-4">{t('settings_page.language_subtitle')}</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => navigate(`/es${location.pathname.replace(/^\/(es|en)/, '')}`)}
            className={lang === 'es' ? 'btn-primary' : 'btn-secondary'}
          >
            Español
          </button>
          <button
            type="button"
            onClick={() => navigate(`/en${location.pathname.replace(/^\/(es|en)/, '')}`)}
            className={lang === 'en' ? 'btn-primary' : 'btn-secondary'}
          >
            English
          </button>
        </div>
      </section>

      <section className="card mb-6">
        <h2 className="font-semibold mb-2">Stripe Connect</h2>
        <p className="text-sm text-neutral-400 mb-4">{t('settings_page.stripe_subtitle')}</p>
        <button
          type="button"
          onClick={() => stripe.mutate()}
          disabled={stripe.isPending}
          className="btn-primary"
        >
          {stripe.isPending ? t('settings_page.stripe_opening') : t('settings_page.stripe_open')}
        </button>
        {stripe.isError && (
          <p className="text-red-400 text-sm mt-3">{(stripe.error as ApiClientError).message}</p>
        )}
      </section>

      <section className="card">
        <h2 className="font-semibold mb-2">{t('settings_page.signout_title')}</h2>
        <p className="text-sm text-neutral-400 mb-4">{t('settings_page.signout_subtitle')}</p>
        <SignOutButton />
      </section>
    </DashboardShell>
  );
}

function AccountSection({ orgId, role }: { orgId: string; role: string }) {
  const { t } = useTranslation('dashboard');
  const user = useAuthStore((s) => s.user);
  const updateProfile = useAuthStore((s) => s.updateProfile);

  const [name, setName] = useState(user?.name ?? '');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setName(user?.name ?? '');
  }, [user?.name]);

  const mutation = useMutation({
    mutationFn: (next: string) => updateProfile({ name: next }),
    onSuccess: () => {
      setSavedAt(Date.now());
    },
  });

  const trimmed = name.trim();
  const dirty = trimmed !== (user?.name ?? '').trim();
  const canSubmit = dirty && trimmed.length >= 1 && trimmed.length <= 120 && !mutation.isPending;

  return (
    <section className="card mb-6">
      <h2 className="font-semibold mb-3">{t('settings_page.account_title')}</h2>
      <form
        className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          mutation.mutate(trimmed);
        }}
      >
        <div className="sm:col-span-2">
          <label
            htmlFor="profile-name"
            className="text-xs uppercase tracking-wider text-neutral-500"
          >
            {t('settings_page.name_label')}
          </label>
          <input
            id="profile-name"
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setSavedAt(null);
            }}
            placeholder={t('settings_page.name_placeholder')}
            maxLength={120}
            className="input mt-2"
          />
          <p className="text-xs text-neutral-500 mt-1">{t('settings_page.name_help')}</p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-wider text-neutral-500">Email</p>
          <p className="text-neutral-200 mt-2 break-all">{user?.email}</p>
          <p className="text-xs text-neutral-500 mt-1">{t('settings_page.email_not_editable')}</p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-wider text-neutral-500">
            {t('settings_page.role_label')}
          </p>
          <p className="text-neutral-200 mt-2 capitalize">{role}</p>
          <p className="text-xs text-neutral-500 mt-1 font-mono break-all">org · {orgId}</p>
        </div>

        <div className="sm:col-span-2 flex flex-wrap items-center gap-3 pt-1">
          <button type="submit" disabled={!canSubmit} className="btn-primary">
            {mutation.isPending ? t('settings_page.saving') : t('settings_page.save_changes')}
          </button>
          {dirty && !mutation.isPending && (
            <button
              type="button"
              onClick={() => {
                setName(user?.name ?? '');
                setSavedAt(null);
              }}
              className="text-sm text-neutral-400 hover:text-neutral-200"
            >
              {t('settings_page.discard')}
            </button>
          )}
          {!dirty && savedAt && (
            <span className="text-sm text-emerald-400">{t('settings_page.saved_ok')}</span>
          )}
          {mutation.isError && (
            <span className="text-sm text-red-400">
              {(mutation.error as ApiClientError).message}
            </span>
          )}
        </div>
      </form>
    </section>
  );
}

function SignOutButton() {
  const { t } = useTranslation('dashboard');
  const logout = useAuthStore((s) => s.logout);
  const { lang } = useParams();
  return (
    <button
      type="button"
      onClick={() => {
        void logout().then(() => {
          window.location.href = `/${lang ?? 'es'}`;
        });
      }}
      className="btn-secondary"
    >
      {t('settings_page.signout_button')}
    </button>
  );
}
